import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Users, TrendingUp, Star, Award, Briefcase, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { mockJobs, mockTestimonials, mockServices, mockExecutiveRoles, mockClients } from '../data/mock';
import { toast } from 'sonner';

const Home = () => {
  const [email, setEmail] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const featuredJobs = mockJobs.filter(job => job.featured).slice(0, 3);

  // Auto-rotate carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % mockServices.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps intentional: we only want to set up the interval once on mount

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email) {
      toast.success('Thank you for subscribing to our newsletter!');
      setEmail('');
    }
  };

  const iconMap = {
    'Building2': Building2,
    'Users': Users,
    'TrendingUp': TrendingUp,
    'Award': Award
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % mockServices.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + mockServices.length) % mockServices.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Sydney Harbour Background */}
      <section 
        className="relative pt-40 pb-32 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-[750px] flex items-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?q=80&w=2070)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-gray-900/80 to-brand-500/70"></div>

        {/* Content */}
        <div className="max-w-7xl mx-auto relative z-10 w-full">
          <div className="text-center max-w-5xl mx-auto">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8 leading-[0.95] tracking-tight">
              Connecting <span className="text-brand-400">Talent</span><br className="hidden sm:block" /> with Opportunity
            </h1>
            <p className="text-xl md:text-2xl text-white/95 mb-3 leading-relaxed font-medium max-w-3xl mx-auto">
              Australia's premier recruitment specialists in real estate, property, and strata management.
            </p>
            <p className="text-xl md:text-2xl text-white/95 mb-12 leading-relaxed font-medium">
              We find exceptional talent for exceptional organisations.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/jobs">
                <Button size="lg" className="bg-brand-400 hover:bg-brand-500 text-white font-bold text-lg px-12 py-8 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105">
                  Browse Jobs
                  <ArrowRight className="ml-2" size={24} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-3 border-white text-white hover:bg-white hover:text-brand-400 font-bold text-lg px-12 py-8 bg-transparent backdrop-blur-sm shadow-2xl transition-all duration-300 hover:scale-105">
                  For Employers
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8 text-center">
            <div>
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2 sm:mb-3">500+</div>
              <p className="text-white/95 text-xs sm:text-sm md:text-base lg:text-lg leading-tight">Successful Placements</p>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2 sm:mb-3">150+</div>
              <p className="text-white/95 text-xs sm:text-sm md:text-base lg:text-lg leading-tight">Partner Organisations</p>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2 sm:mb-3">95%</div>
              <p className="text-white/95 text-xs sm:text-sm md:text-base lg:text-lg leading-tight">Client Satisfaction</p>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2 sm:mb-3">56</div>
              <p className="text-white/95 text-xs sm:text-sm md:text-base lg:text-lg leading-tight">Years Combined Experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Interactive Carousel */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-3 sm:mb-4 md:mb-6 tracking-tight leading-tight">
              Our <span className="text-brand-400">Specialisations</span>
            </h2>
            <p className="text-gray-600 text-base sm:text-lg md:text-xl max-w-2xl mx-auto font-medium leading-relaxed px-4">
              Focused expertise in the property and strata sectors across Australia
            </p>
          </div>

          {/* Carousel Container */}
          <div className="relative">
            {/* Main Carousel */}
            <div className="relative h-auto min-h-[600px] sm:min-h-[550px] md:min-h-[500px] mb-8 sm:mb-10 md:mb-12">
              {mockServices.map((service, index) => {
                const Icon = iconMap[service.icon];
                const isActive = index === currentSlide;
                const isPrev = index === (currentSlide - 1 + mockServices.length) % mockServices.length;
                const isNext = index === (currentSlide + 1) % mockServices.length;
                
                // Determine slide position classes
                let positionClasses = 'opacity-0 scale-90 z-0';
                if (isActive) {
                  positionClasses = 'opacity-100 scale-100 z-20 translate-x-0';
                } else if (isPrev) {
                  positionClasses = 'opacity-0 scale-90 z-10 -translate-x-full';
                } else if (isNext) {
                  positionClasses = 'opacity-0 scale-90 z-10 translate-x-full';
                }
                
                return (
                  <div
                    key={service.id}
                    className={`absolute inset-0 transition-all duration-700 ease-in-out ${positionClasses}`}
                  >
                    <Card className="bg-white border-2 border-gray-200 hover:border-brand-400 shadow-2xl h-full mx-auto max-w-5xl">
                      <CardContent className="p-6 sm:p-8 md:p-12 h-full flex flex-col md:flex-row items-start md:items-center gap-6 sm:gap-8 md:gap-12">
                        <div className="flex-1 w-full">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-brand-50 rounded-2xl flex items-center justify-center mb-4 sm:mb-6 md:mb-8 shadow-lg">
                            <Icon size={window.innerWidth < 640 ? 32 : window.innerWidth < 768 ? 40 : 48} className="text-brand-400" />
                          </div>
                          <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-3 sm:mb-4 md:mb-6 tracking-tight leading-tight">{service.title}</h3>
                          <p className="text-gray-600 text-base sm:text-lg md:text-xl leading-relaxed">{service.description}</p>
                        </div>
                        <div className="flex-1 w-full">
                          <div className="bg-gradient-to-br from-brand-500/10 to-transparent rounded-2xl p-4 sm:p-6 md:p-8 h-full flex flex-col">
                            <h4 className="text-lg sm:text-xl md:text-2xl font-black text-gray-900 mb-3 sm:mb-4 md:mb-6 tracking-tight">Key Benefits:</h4>
                            <ul className="space-y-2 sm:space-y-3 md:space-y-4">
                              {service.benefits.map((benefit) => (
                                <li key={benefit} className="flex items-start text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
                                  <span className="text-brand-400 mr-2 sm:mr-3 md:mr-4 mt-0.5 sm:mt-1 font-bold text-lg sm:text-xl md:text-2xl flex-shrink-0">✓</span>
                                  <span>{benefit}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-2 sm:left-0 top-1/2 -translate-y-1/2 sm:-translate-x-4 w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-white border-2 border-brand-400 rounded-full flex items-center justify-center text-brand-400 hover:bg-brand-400 hover:text-white transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-110 z-30"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-2 sm:right-0 top-1/2 -translate-y-1/2 sm:translate-x-4 w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-white border-2 border-brand-400 rounded-full flex items-center justify-center text-brand-400 hover:bg-brand-400 hover:text-white transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-110 z-30"
              aria-label="Next slide"
            >
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
            </button>

            {/* Dot Indicators */}
            <div className="flex justify-center gap-2 sm:gap-3">
              {mockServices.map((service, index) => (
                <button
                  key={service.id}
                  onClick={() => goToSlide(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentSlide
                      ? 'w-8 sm:w-10 md:w-12 h-2 sm:h-2.5 md:h-3 bg-brand-400'
                      : 'w-2 sm:w-2.5 md:w-3 h-2 sm:h-2.5 md:h-3 bg-gray-300 hover:bg-brand-300'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          <div className="text-center mt-16">
            <Link to="/services">
              <Button variant="outline" className="border-2 border-brand-400 text-brand-400 hover:bg-brand-400 hover:text-white font-bold px-8 py-6 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-xl">
                View All Services
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Executive Search Section */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20">
            <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 md:mb-8 backdrop-blur-sm">
              <Award className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 text-white" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white mb-3 sm:mb-4 md:mb-6 tracking-tight leading-tight px-2">
              Executive Search Services
            </h2>
            <p className="text-white/90 text-base sm:text-lg md:text-xl max-w-3xl mx-auto leading-relaxed px-4">
              Specialised headhunting for senior leadership positions in property, real estate, and strata management sectors
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 mb-8 sm:mb-12 md:mb-16">
            {mockExecutiveRoles.map((role) => (
              <Card key={role.id} className="bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl">
                <CardContent className="p-4 sm:p-6 md:p-8">
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <Briefcase className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white flex-shrink-0" />
                    <span className="text-xs bg-white/20 text-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-full font-semibold backdrop-blur-sm">
                      {role.seniority}
                    </span>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3 leading-tight">{role.title}</h3>
                  <p className="text-white/80 text-sm sm:text-base leading-relaxed">{role.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 lg:p-16 border border-white/20 shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-12 items-start">
              <div>
                <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-white mb-4 sm:mb-5 md:mb-6 tracking-tight leading-tight">
                  Confidential Executive Placement
                </h3>
                <p className="text-white/90 mb-6 sm:mb-7 md:mb-8 text-base sm:text-lg leading-relaxed">
                  Our executive search practice specialises in identifying and securing top-tier leadership talent for:
                </p>
                <ul className="space-y-3 sm:space-y-4 text-white/90 text-sm sm:text-base md:text-lg">
                  <li className="flex items-start">
                    <span className="text-white mr-2 sm:mr-3 md:mr-4 mt-0.5 sm:mt-1 text-lg sm:text-xl font-bold flex-shrink-0">✓</span>
                    <span>General Managers & Chief Officers</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-white mr-2 sm:mr-3 md:mr-4 mt-0.5 sm:mt-1 text-lg sm:text-xl font-bold flex-shrink-0">✓</span>
                    <span>Sales Managers & Directors</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-white mr-2 sm:mr-3 md:mr-4 mt-0.5 sm:mt-1 text-lg sm:text-xl font-bold flex-shrink-0">✓</span>
                    <span>Department Heads & Senior Leaders</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-white mr-2 sm:mr-3 md:mr-4 mt-0.5 sm:mt-1 text-lg sm:text-xl font-bold flex-shrink-0">✓</span>
                    <span>Property Management Executives</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 shadow-2xl">
                <h4 className="text-lg sm:text-xl md:text-2xl font-black text-gray-900 mb-4 sm:mb-5 md:mb-6 tracking-tight leading-tight">Our Executive Search Process</h4>
                <div className="space-y-4 sm:space-y-5 md:space-y-6">
                  <div className="flex items-start">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-brand-400 text-white rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mr-3 sm:mr-4 font-black text-base sm:text-lg shadow-md">
                      1
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base md:text-lg">Confidential Consultation</h5>
                      <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed">Understanding your leadership needs and culture</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-brand-400 text-white rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mr-3 sm:mr-4 font-black text-base sm:text-lg shadow-md">
                      2
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base md:text-lg">Targeted Headhunting</h5>
                      <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed">Proactive identification of top performers</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-brand-400 text-white rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mr-3 sm:mr-4 font-black text-base sm:text-lg shadow-md">
                      3
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base md:text-lg">Executive Assessment</h5>
                      <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed">Comprehensive evaluation and reference checks</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-brand-400 text-white rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mr-3 sm:mr-4 font-black text-base sm:text-lg shadow-md">
                      4
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base md:text-lg">Seamless Placement</h5>
                      <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed">Negotiation support and onboarding assistance</p>
                    </div>
                  </div>
                </div>
                <Link to="/contact">
                  <Button className="w-full mt-6 sm:mt-7 md:mt-8 bg-brand-400 hover:bg-brand-500 text-white font-bold py-4 sm:py-5 md:py-6 text-sm sm:text-base md:text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                    Request Executive Search Consultation
                    <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Featured Jobs Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6 tracking-tight leading-tight">
              Featured <span className="text-brand-400">Opportunities</span>
            </h2>
            <p className="text-gray-600 text-xl font-medium leading-relaxed">
              Explore our latest property and strata management positions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredJobs.map((job) => (
              <Card key={job.id} className="bg-white border-gray-200 hover:border-brand-400 hover:shadow-2xl transition-all duration-300 group cursor-pointer hover:-translate-y-2">
                <CardContent className="p-8">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 bg-brand-50 rounded-xl flex items-center justify-center group-hover:bg-brand-100 group-hover:scale-110 transition-all duration-300">
                      <Building2 size={28} className="text-brand-400" />
                    </div>
                    <span className="text-xs bg-brand-50 text-brand-400 px-3 py-1.5 rounded-full font-semibold">
                      {job.type}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-brand-400 transition-colors duration-200 leading-tight">
                    {job.title}
                  </h3>
                  <p className="text-gray-600 text-base mb-4">{job.location}</p>
                  <p className="text-brand-400 font-bold mb-5 text-lg">{job.salary}</p>
                  <p className="text-gray-700 text-base mb-6 line-clamp-2 leading-relaxed">{job.description}</p>
                  <Link to={`/jobs`}>
                    <Button variant="ghost" className="text-brand-400 hover:text-brand-500 hover:bg-brand-50 p-0 font-bold">
                      View Details
                      <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link to="/jobs">
              <Button size="lg" className="bg-brand-400 hover:bg-brand-500 text-white font-bold px-10 py-7 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                View All Jobs
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6 tracking-tight leading-tight">
              What Our <span className="text-brand-400">Clients Say</span>
            </h2>
            <p className="text-gray-600 text-xl font-medium leading-relaxed">
              Real reviews from our satisfied clients on Google
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-white border-gray-200 hover:border-brand-400/40 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-10">
                  <div className="flex mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`star-${testimonial.id}-${i}`} size={24} className="text-brand-400 fill-brand-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-8 italic text-base leading-relaxed line-clamp-6">"{testimonial.content}"</p>
                  <div className="border-t border-gray-200 pt-6">
                    <p className="text-gray-900 font-bold text-lg">{testimonial.name}</p>
                    <p className="text-gray-600 text-sm mt-1">{testimonial.position}</p>
                    <p className="text-brand-400 text-sm font-semibold mt-1">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* Client Logos Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6 tracking-tight leading-tight">
              Trusted by <span className="text-brand-400">Leading Brands</span>
            </h2>
            <p className="text-gray-600 text-xl font-medium leading-relaxed">
              Partnering with Australia's most respected real estate agencies
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 items-center">
            {mockClients.map((client) => (
              <div 
                key={client.name} 
                className="flex items-center justify-center p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-brand-400 hover:shadow-xl transition-all duration-300 h-40 hover:-translate-y-1"
              >
                <img 
                  src={client.logo} 
                  alt={client.name}
                  className="max-w-full max-h-24 w-auto h-auto object-contain grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-br from-brand-50 via-white to-brand-50/50 border-brand-200 shadow-xl hover:shadow-2xl transition-all duration-300">
            <CardContent className="p-12 md:p-16 text-center">
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight leading-tight">
                Stay Updated
              </h2>
              <p className="text-gray-700 mb-10 text-xl leading-relaxed">
                Subscribe to receive the latest job opportunities and industry insights
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 py-6 text-lg shadow-md"
                />
                <Button type="submit" className="bg-brand-400 hover:bg-brand-500 text-white font-bold whitespace-nowrap px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  Subscribe
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Join Our Team CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-400 via-brand-500 to-brand-600">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="text-white">
              <h2 className="text-4xl md:text-5xl font-black mb-6 tracking-tight leading-tight">
                Are You a Top <span className="text-gray-900">Recruiter?</span>
              </h2>
              <p className="text-xl mb-8 leading-relaxed text-white/95">
                Join Sydney's fastest-growing property recruitment agency. Keep <strong>60% of what you earn</strong> with full back and front-end support.
              </p>
              <ul className="space-y-4 mb-10 text-lg">
                <li className="flex items-start">
                  <span className="text-gray-900 mr-4 mt-1 text-2xl font-bold">✓</span>
                  <span>60% commission splits - Industry-leading</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-900 mr-4 mt-1 text-2xl font-bold">✓</span>
                  <span>Full administrative & marketing support</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-900 mr-4 mt-1 text-2xl font-bold">✓</span>
                  <span>Established client base & brand reputation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gray-900 mr-4 mt-1 text-2xl font-bold">✓</span>
                  <span>Uncapped earning potential</span>
                </li>
              </ul>
              <Link to="/careers">
                <Button size="lg" className="bg-white text-brand-400 hover:bg-gray-100 font-bold text-lg px-12 py-8 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105">
                  Join Our Team
                  <ArrowRight className="ml-2" size={24} />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Card className="bg-white/10 backdrop-blur-md border-white/20">
                <CardContent className="p-8 text-center text-white">
                  <div className="text-5xl font-black mb-3">60%</div>
                  <p className="text-lg">Commission Split</p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 backdrop-blur-md border-white/20">
                <CardContent className="p-8 text-center text-white">
                  <div className="text-5xl font-black mb-3">$500K+</div>
                  <p className="text-lg">Top Billers</p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 backdrop-blur-md border-white/20">
                <CardContent className="p-8 text-center text-white">
                  <div className="text-5xl font-black mb-3">100%</div>
                  <p className="text-lg">Support</p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 backdrop-blur-md border-white/20">
                <CardContent className="p-8 text-center text-white">
                  <div className="text-5xl font-black mb-3">Zero</div>
                  <p className="text-lg">BS Policy</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-8 tracking-tight leading-tight">
            Ready to <span className="text-brand-400">Grow</span> Your Career?
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed">
            Connect with Australia's leading property and strata recruitment specialists today
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-brand-400 hover:bg-brand-500 text-white font-bold text-lg px-12 py-8 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105">
                Get In Touch
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
            <Link to="/about">
              <Button size="lg" variant="outline" className="border-3 border-brand-400 text-brand-400 hover:bg-brand-400 hover:text-white font-bold text-lg px-12 py-8 shadow-2xl transition-all duration-300 hover:scale-105">
                Learn About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
